import {useCallback, useRef, useState, type ChangeEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {IconLoadError, loadIconImageFile} from './loadIconFile'
import {addBreadcrumb} from '../../../common/errors/breadcrumb'
import {reportError} from '../../../common/errors/reportError'
import {trackEvent} from '../../../common/analytics/trackEvent'

interface IconSourceUploadProps {
  image: HTMLImageElement | null
  onImageLoaded: (image: HTMLImageElement) => void
  onClear: () => void
}

const IconSourceUpload = ({image, onImageLoaded, onClear}: IconSourceUploadProps) => {
  const {t} = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file) return
      setError(null)
      setBusy(true)
      addBreadcrumb('icon-gen', 'source-picked', 'info', {size: file.size, type: file.type})
      const eventName = image ? 'icon_image_replaced' : 'icon_image_uploaded'
      trackEvent(eventName, {format: file.type, size_bytes: file.size})
      try {
        const img = await loadIconImageFile(file)
        addBreadcrumb('icon-gen', 'source-loaded', 'info', {w: img.width, h: img.height})
        onImageLoaded(img)
      } catch (err) {
        if (err instanceof IconLoadError) {
          setError(t(`iconGenerator.upload.${err.key}`))
        } else {
          reportError(err, 'icon-gen-load', {size: file.size, type: file.type})
          setError(t('iconGenerator.upload.failed'))
        }
      } finally {
        setBusy(false)
      }
    },
    [onImageLoaded, t, image],
  )

  const handlePick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  return (
    <div className="icg-upload">
      {image && (
        <div className="icg-upload-thumb">
          <img src={image.src} alt={t('iconGenerator.upload.previewAlt')} />
        </div>
      )}

      <div className="icg-upload-actions">
        <button type="button" className="icg-btn icg-btn-secondary" onClick={handlePick} disabled={busy}>
          {busy
            ? t('iconGenerator.upload.busy')
            : image
              ? t('iconGenerator.upload.replace')
              : t('iconGenerator.upload.pick')}
        </button>
        {image && (
          <button type="button" className="icg-btn icg-btn-ghost" onClick={onClear} disabled={busy}>
            {t('iconGenerator.upload.clear')}
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        style={{display: 'none'}}
      />

      <p className="icg-hint">{t('iconGenerator.upload.hint')}</p>
      {error && <p className="icg-error">{error}</p>}
    </div>
  )
}

export default IconSourceUpload
